import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Github, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n";

const CTASection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-20 sm:py-28 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-secondary/20 to-accent/30 blur-3xl opacity-50" />
          
          {/* Content card */}
          <div className="relative glass-card rounded-3xl p-8 sm:p-12 lg:p-16 text-center overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-br-full" />
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-accent/20 to-transparent rounded-tl-full" />
            
            {/* Icon */}
            <div className="relative inline-flex mb-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-primary-foreground" />
              </div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary to-accent blur-xl opacity-50" />
            </div>
            
            {/* Content */}
            <div className="relative space-y-6">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">
                {t('cta.title')}{" "}
                <span className="text-gradient">{t('cta.titleHighlight')}</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                {t('cta.description')}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center pt-4">
                <Button variant="hero" size="xl" asChild className="group">
                  <a
                    href="https://apps.apple.com/app/lumiere-openclaw-ai-agents/id6758534255"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                    </svg>
                    {t('cta.ctaPrimary')}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </Button>
                <Button variant="hero" size="xl" asChild className="group">
                  <a
                    href="https://chromewebstore.google.com/detail/lumiere/ppnnimejlhphcjckocpiinjmpmdfhhmg"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.49 0 2.87.47 4.01 1.26L7.8 17.65A7.007 7.007 0 0 1 5 12c0-3.87 3.13-7 7-7zm0 14c-1.49 0-2.87-.47-4.01-1.26L16.2 6.35A7.007 7.007 0 0 1 19 12c0 3.87-3.13 7-7 7z"/>
                      <circle cx="12" cy="12" r="3.5"/>
                    </svg>
                    {t('cta.ctaChrome')}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </Button>
                <Button variant="hero-outline" size="xl" asChild>
                  <a
                    href="https://github.com/ajamaica/lumiere"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Github className="w-5 h-5" />
                    {t('cta.ctaSecondary')}
                  </a>
                </Button>
              </div>
              
              {/* Requirements */}
              <p className="text-sm text-muted-foreground pt-4">
                {t('cta.requirement')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
